import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, FileText, MailCheck, Clock, AlertTriangle } from "lucide-react";
import { INDUSTRIES } from "../../lib/industries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_DOMAINS = [
  "gmail.com", "googlemail.com", "mail.ru", "inbox.ru", "list.ru", "bk.ru", "internet.ru",
  "yandex.ru", "ya.ru", "yandex.com", "yandex.by", "yandex.kz", "yandex.ua", "narod.ru",
  "rambler.ru", "lenta.ru", "ro.ru", "icloud.com", "me.com", "mac.com", "outlook.com",
  "hotmail.com", "live.com", "msn.com", "yahoo.com", "ymail.com", "aol.com", "proton.me",
  "protonmail.com", "gmx.com", "zoho.com", "mail.com", "qip.ru", "pochta.ru",
];

export const VERSION_OPTIONS = [
  {
    id: "short",
    title: "Краткая версия",
    pages: "25+ страниц",
    desc: "Ключевые выводы, модель трёх типов спроса, рейтинг отраслей и главные рекомендации.",
  },
  {
    id: "full",
    title: "Полная версия",
    pages: "150+ страниц",
    desc: "Полная методология, разбор 50+ сегментов, данные глубинных интервью и практические рекомендации. Нужен рабочий email.",
  },
];

const EMPTY = {
  first_name: "", last_name: "", company_name: "", industry: "", position: "",
  email: "", phone: "", consent_pdn: false, consent_marketing: false,
};

const isWorkEmail = (email) => !FREE_DOMAINS.includes((email.split("@")[1] || "").toLowerCase().trim());

export default function ResearchAccessForm() {
  const [version, setVersion] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const full = version === "full";
  const emailOk = /\S+@\S+\.\S+/.test(form.email);
  const workEmailError = full && emailOk && !isWorkEmail(form.email);

  const valid =
    version && form.first_name.trim() && form.last_name.trim() && form.company_name.trim() &&
    emailOk && !workEmailError && form.phone.trim() && form.consent_pdn &&
    (!full || (form.industry && form.position.trim()));

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form, version };
      if (!full) { delete payload.industry; delete payload.position; }
      const r = await axios.post(`${API}/research/leads`, payload);
      setResult({ ...r.data, email: form.email });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Не удалось отправить форму. Проверьте соединение и попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestOther = () => {
    setResult(null);
    setVersion(version === "full" ? "short" : "full");
  };

  return (
    <section id="lead-form" className="max-w-screen-xl mx-auto px-6 sm:px-8 py-16 md:py-28 grid md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <div className="label-mono">ДОСТУП</div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl mt-3 font-medium">Получить исследование</h2>
        <p className="mt-5 text-foreground/70 leading-relaxed">
          Выберите версию отчёта и оставьте контакты. Ссылка на загрузку результатов
          исследования будет отправлена на указанный email.
        </p>
        <div className="mt-8 flex items-center gap-3 text-sm text-foreground/60">
          <FileText size={16} />
          <span>PDF · {version === "full" ? "150+ страниц" : version === "short" ? "25+ страниц" : "25+ или 150+ страниц"}</span>
        </div>
      </div>

      <div className="md:col-span-7 md:col-start-6">
        {result ? (
          <SuccessPanel result={result} version={version} onOther={requestOther} />
        ) : (
          <div className="space-y-10">
            <VersionPicker value={version} onChange={setVersion} />
            {version && (
              <LeadFields
                form={form} set={set} full={full} workEmailError={workEmailError}
                error={error} valid={valid} submitting={submitting} submit={submit}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function VersionPicker({ value, onChange }) {
  return (
    <div data-testid="research-version-picker">
      <div className="label-mono mb-4">ШАГ 1 · ВЕРСИЯ ОТЧЁТА</div>
      <div className="grid sm:grid-cols-2 gap-4">
        {VERSION_OPTIONS.map((v) => {
          const active = value === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => onChange(v.id)}
              data-testid={`research-version-${v.id}`}
              aria-pressed={active}
              className={`text-left p-6 border transition-colors duration-200 ${
                active ? "border-black bg-[hsl(var(--card))] shadow-[inset_0_0_0_1px_black]" : "border-black/15 hover:border-black/50"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xl font-medium">{v.title}</span>
                <span className={`w-4 h-4 rounded-full border ${active ? "border-black bg-black" : "border-black/40"}`} />
              </div>
              <div className="mt-2 label-mono">{v.pages}</div>
              <p className="mt-4 text-sm text-foreground/70 leading-relaxed">{v.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LeadFields({ form, set, full, workEmailError, error, valid, submitting, submit }) {
  return (
    <div className="space-y-7" data-testid="research-form">
      <div className="label-mono">ШАГ 2 · КОНТАКТЫ</div>
      <div className="grid md:grid-cols-2 gap-7">
        <Field label="Имя *" testid="research-firstname">
          <input className="field-input" value={form.first_name} onChange={set("first_name")} placeholder="Иван" />
        </Field>
        <Field label="Фамилия *" testid="research-lastname">
          <input className="field-input" value={form.last_name} onChange={set("last_name")} placeholder="Иванов" />
        </Field>
      </div>
      <Field label="Название компании *" testid="research-company">
        <input className="field-input" value={form.company_name} onChange={set("company_name")} placeholder='ООО «Компания»' />
      </Field>
      {full && (
        <>
          <Field label="Сфера деятельности компании *" testid="research-industry">
            <select className="field-input" value={form.industry} onChange={set("industry")} style={{ appearance: "auto" }}>
              <option value="">Выберите отрасль…</option>
              {INDUSTRIES.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
            </select>
          </Field>
          <Field label="Позиция в компании *" testid="research-position">
            <input className="field-input" value={form.position} onChange={set("position")} placeholder="Например: Head of Innovation" />
          </Field>
        </>
      )}
      <div className="grid md:grid-cols-2 gap-7">
        <Field label={full ? "Рабочий e-mail *" : "E-mail *"} testid="research-email">
          <input className="field-input" value={form.email} onChange={set("email")} placeholder="you@company.ru" type="email" />
          {workEmailError && (
            <div className="mt-2 text-xs text-[hsl(var(--destructive))]" data-testid="research-work-email-error">
              Для полной версии нужен рабочий email — личные адреса (gmail, mail.ru, yandex и т.п.) не подходят.
            </div>
          )}
        </Field>
        <Field label="Телефон *" testid="research-phone">
          <input className="field-input" value={form.phone} onChange={set("phone")} placeholder="+7" inputMode="tel" />
        </Field>
      </div>
      <Consents form={form} set={set} />
      {error && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="research-error">{error}</div>}
      <div className="pt-2 space-y-3">
        <button className="btn-primary" disabled={!valid || submitting} onClick={submit} data-testid="research-submit-btn">
          {submitting ? "Отправка…" : "Получить ссылку на email"} <ArrowRight size={16} />
        </button>
        <p className="text-xs text-foreground/55" data-testid="research-email-note">
          Ссылка на загрузку результатов исследования будет отправлена на указанную почту.
        </p>
      </div>
    </div>
  );
}

function Consents({ form, set }) {
  return (
    <>
      <label className="flex items-start gap-3 pt-3 cursor-pointer" data-testid="research-consent">
        <input type="checkbox" checked={form.consent_pdn} onChange={set("consent_pdn")}
          className="mt-1 w-4 h-4 accent-black cursor-pointer" data-testid="research-consent-checkbox" />
        <span className="text-sm text-foreground/75 leading-relaxed">
          Я даю согласие на обработку персональных данных в соответствии с{" "}
          <Link to="/privacy" target="_blank" className="hop-link">Политикой обработки персональных данных</Link>.
        </span>
      </label>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="research-marketing">
        <input type="checkbox" checked={form.consent_marketing} onChange={set("consent_marketing")}
          className="mt-1 w-4 h-4 accent-black cursor-pointer" data-testid="research-marketing-checkbox" />
        <span className="text-sm text-foreground/75 leading-relaxed">
          Согласен на получение рассылок информационного и рекламного характера о деятельности компании
          и её партнёров, публикациях, мероприятиях, новостях, продуктах и услугах.
        </span>
      </label>
    </>
  );
}

const STATUS_COPY = {
  sent: {
    icon: MailCheck,
    label: "СТАТУС / ОТПРАВЛЕНО",
    title: "Ссылка на загрузку отправлена на вашу почту",
  },
  pending: {
    icon: Clock,
    label: "СТАТУС / ПРИНЯТО",
    title: "Заявка принята",
  },
  failed: {
    icon: AlertTriangle,
    label: "СТАТУС / ПРИНЯТО",
    title: "Заявка принята, письмо отправим чуть позже",
  },
};

function SuccessPanel({ result, version, onOther }) {
  const c = STATUS_COPY[result.status] || STATUS_COPY.pending;
  const Icon = c.icon;
  const other = VERSION_OPTIONS.find((v) => v.id !== version);
  return (
    <div className="border border-black/15 bg-[hsl(var(--card))] p-6 sm:p-10 md:p-14" data-testid="research-thankyou" data-status={result.status}>
      <div className="flex items-center gap-3">
        <Icon size={18} className="text-[hsl(var(--accent))]" />
        <div className="label-mono">{c.label}</div>
      </div>
      <h3 className="mt-4 text-2xl md:text-3xl font-medium leading-snug">{c.title}</h3>
      <div className="mt-6 text-foreground/75 leading-relaxed space-y-3" data-testid="research-thankyou-text">
        {result.status === "sent" && (
          <>
            <p>Мы отправили письмо на <b className="break-all">{result.email}</b>. Если его нет во «Входящих» в течение 5 минут — проверьте папку «Спам» или «Промоакции».</p>
            <p className="text-sm">Ссылка персональная: действует {result.ttl_hours} ч, количество скачиваний — {result.max_downloads}.</p>
          </>
        )}
        {result.status === "pending" && (
          <p>Исследование готовится к публикации. Ссылка на загрузку придёт на <b className="break-all">{result.email}</b> сразу после выхода отчёта.</p>
        )}
        {result.status === "failed" && (
          <p>Не удалось отправить письмо на <b className="break-all">{result.email}</b> прямо сейчас. Мы повторим отправку в ближайшее время. Если письмо не придёт, напишите на <a className="hop-link" href="mailto:hello@hop.agency">hello@hop.agency</a>.</p>
        )}
      </div>
      <div className="mt-10 flex flex-wrap gap-4">
        <button className="btn-secondary" onClick={onOther} data-testid="research-request-other">
          Запросить {other.title.toLowerCase()}
        </button>
        <Link to="/" className="btn-ghost" data-testid="research-back-home">На главную</Link>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block">
      <div className="label-mono mb-2">{label}</div>
      <div data-testid={testid}>{children}</div>
    </label>
  );
}
